import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmDialogService } from '../../services/confirm-dialog-service';
import { AdminService } from '../../services/admin-service';
import { ToastService } from '../../services/toast-service';
import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import {
  TableHeadSelectionComponent,
  TableRowSelectionComponent,
} from '../../components/select-column/select-column';
import { TableButtons } from '../../components/table-buttons/table-buttons';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmDialog } from '../../components/confirm-dialog/confirm-dialog';
import { Button } from '../../components/button/button';
import { Modal } from '../../components/modal/modal';
import { InputComponent } from '../../components/input/input';
import { Table } from '../../components/table/table';
import { BackButton } from '../../components/back-button/back-button';
import { Loading } from '../../components/loading/loading';
import { CnpjMaskDirective } from '../../directive/cnpjDirective';
import { PhoneMask } from '../../directive/phone';
import { CepMask } from '../../directive/cep-mask';
import { HttpErrorResponse } from '@angular/common/http';

export interface TMerchant {
  merchantId: string;
  merchantLabel: string;
  cnpj: string;
  clientId: string;
  checkouts: any;
}

@Component({
  selector: 'app-merchants',
  imports: [
    ConfirmDialog,
    Button,
    Modal,
    InputComponent,
    ReactiveFormsModule,
    Table,
    BackButton,
    RouterLink,
    Loading,
    CnpjMaskDirective,
    PhoneMask,
    CepMask,
  ],
  templateUrl: './merchants.html',
  styleUrl: './merchants.scss',
})
export class Merchants implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  loading = false;
  clientId = signal('');
  clientLabel = signal('');

  showModal = false;
  showConfirmDialog = false;
  message = '';

  data: any[] = [];

  constructor(
    private confirmDialogService: ConfirmDialogService,
    private adminService: AdminService,
    private toast: ToastService,
  ) {
    this.activatedRoute.params.subscribe((params) => {
      this.clientId.set(params['id']);
      this.clientLabel.set(params['label']);
    });

    this.confirmDialogService.showDialog$.subscribe(({ message }) => {
      this.message = message;
      this.showConfirmDialog = true;
    });
  }

  ngOnInit(): void {
    this.getMerchants();
  }

  getMerchants() {
    this.loading = true;
    this.adminService.getMerchants(this.clientId()).subscribe((resp) => (this.data = resp));
    this.loading = false;
  }

  columns: ColumnDef<TMerchant>[] = [
    {
      id: 'select-col',
      header: () => flexRenderComponent(TableHeadSelectionComponent),
      cell: () => flexRenderComponent(TableRowSelectionComponent),
    },
    {
      accessorKey: 'merchantLabel',
      header: 'Merchant Label',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'merchantId',
      header: 'Merchant ID',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'cnpj',
      header: 'CNPJ',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'checkouts',
      header: 'Checkouts',
      cell: (info) => {
        const checkouts = (info.getValue() as Array<any>) ?? [];
        const checkoutsLength = checkouts.length == null ? 0 : checkouts.length;

        return `<a class="text-primary">( ${checkoutsLength} )</a>`;
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const merchant = row.original;
        return flexRenderComponent(TableButtons, {
          inputs: {
            title: 'trash',
          },
          outputs: {
            clickEvent: () => this.deletarMerchant(merchant),
          },
        });
      },
    },
  ];

  merchantRegister = new FormGroup({
    razaoSocial: new FormControl('', [Validators.required, Validators.minLength(3)]),
    nomeFantasia: new FormControl('', [Validators.required, Validators.minLength(3)]),
    email: new FormControl('', [Validators.required, Validators.minLength(3)]),
    telefone: new FormControl('', [Validators.required, Validators.minLength(3)]),
    cep: new FormControl('', [Validators.required, Validators.minLength(3)]),
    endereco: new FormControl('', [Validators.required, Validators.minLength(3)]),
    CNPJ: new FormControl('', [Validators.required, Validators.minLength(5)]),
    mcc: new FormControl('', [Validators.required, Validators.minLength(3)]),
    numeroLogico: new FormControl('', [Validators.required, Validators.minLength(3)]),
  });

  openModal() {
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
  }

  salvar() {
    if (this.merchantRegister.invalid)
      this.toast.show({
        message: 'Error',
        type: 'warn',
      });

    this.adminService
      .registerMerchant({ ...this.merchantRegister.value, clientId: this.clientId() })
      .subscribe({
        next: () => {
          this.toast.show({
            type: 'success',
            message: 'Cliente Adicionado',
          });
          this.getMerchants();
          this.closeModal();
        },
        error: (resp: HttpErrorResponse) => {
          this.toast.show({
            type: 'danger',
            message: resp.error,
          });
        },
      });
  }

  onConfirm() {
    this.confirmDialogService.confirm();
    this.showConfirmDialog = false;
  }

  onCancel() {
    this.confirmDialogService.cancel();
    this.showConfirmDialog = false;
  }

  deletarMerchant(merchant: TMerchant) {
    this.confirmDialogService
      .requestConfirmation(`Deseja deletar [${merchant.merchantLabel}]?`)
      .subscribe((confirmed) => {
        if (confirmed) {
          this.adminService.deleteMerchant(this.clientId(), merchant.merchantId).subscribe(() => {
            this.toast.show({
              message: 'Cliente deletado com sucesso',
              type: 'info',
            });

            this.getMerchants();
          });
        } else {
          console.log('Ação cancelada');
        }
      });
  }

  get formControls() {
    return Object.keys(this.merchantRegister.controls);
  }
}
